import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  imageIdFormat,
  locationIdFormat,
  packingTemplateIdFormat,
  productFamilyIdFormat,
  productFamilyImageIdFormat,
  productIdFormat,
  productImageIdFormat,
  storageLocationIdFormat,
  supplierIdFormat,
  supplierProductIdFormat,
} from "../../ids";
import {
  AttributeSchema,
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
  HsCodeSchema,
  MeasurementSchema,
  WeightSchema,
} from "../attributes";
import { PackingTemplateSchema } from "../packing-template";

export const ProductBinSchema = z.object({
  storageLocationId: storageLocationIdFormat.schema,
  binName: z.string(),
  locationId: locationIdFormat.schema,
  locationName: z.string(),
  quantity: z.number().int(),
});
export type ProductBin = z.infer<typeof ProductBinSchema>;

const LocationInventorySchema = z.object({
  id: locationIdFormat.schema,
  name: z.string(),
  quantity: z.bigint(),
  buildable: z.bigint(),
  committed: z.bigint(),
  reserved: z.bigint(),
  reservedBuckets: z.array(
    z.object({
      bucketKey: z.string(),
      kind: z.string().nullable(),
      customTypeId: z.string().nullable(),
      quantity: z.bigint(),
    }),
  ),
  available: z.bigint(),
});

export type LocationInventoryData = z.infer<typeof LocationInventorySchema>;

const ImageSchema = z.object({
  id: z.union([productFamilyImageIdFormat.schema, productImageIdFormat.schema]),
  imageId: imageIdFormat.schema.nullish(),
  url: z.string(),
  altText: z.string(),
  thumbhash: z.string().nullish(),
  position: z.number(),
  generated: z.boolean().nullish(),
  feedback: z.enum(["positive", "negative"]).nullish(),
  isDefault: z.boolean(),
});

const SupplierProductSchema_ = z.object({
  id: supplierProductIdFormat.schema,
  supplierId: supplierIdFormat.schema,
  supplierName: z.string(),
  supplierCurrency: z.string().nullable(),
  productTitle: z.string().nullable(),
  supplierSku: z.string().nullable(),
  unitCost: z.number().nullable(),
  currency: z.string().nullable(),
  avgUnitCost: z.number().nullable(),
  mostRecentPoCost: z.number().nullable(),
});

export const SupplierProductSchema: z.ZodType<SupplierProduct> =
  SupplierProductSchema_;
export interface SupplierProduct
  extends z.infer<typeof SupplierProductSchema_> {}

export const ProductDetailsSchema = z.object({
  id: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema,
  title: z.string(),
  displayTitle: z.string(),
  description: z.string(),
  vendor: z.string().nullish(),
  tags: z.array(z.string()),
  price: z.number().nullish(),
  cost: z.number().nullish(),
  currency: z.string().nullish(),
  sku: z.string().nullish(),
  upc: z.string().nullish(),
  barcodes: z.array(z.string()),
  weight: WeightSchema.nullish(),
  length: MeasurementSchema.nullish(),
  width: MeasurementSchema.nullish(),
  height: MeasurementSchema.nullish(),
  taxCode: z.string().nullish(),
  taxable: z.boolean().nullish(),
  physicalItem: z.boolean().nullish(),
  nonPicked: z.boolean().nullish(),
  originCountry: z.string().nullish(),
  hsCode: HsCodeSchema.nullish(),
  source: z.string().nullish(),
  source_id: z.string().nullish(),
  image: z
    .object({
      altText: z.string(),
      url: z.string(),
      thumbhash: z.string().nullish(),
    })
    .nullish(),
  images: z.array(ImageSchema),
  attributes: z.array(AttributeSchema),
  locations: z.array(LocationInventorySchema),
  bins: z.array(ProductBinSchema).optional(),
  supplierProducts: z.array(SupplierProductSchema),
  packingTemplates: z.array(PackingTemplateSchema).nullish().default([]),
  defaultPackingTemplateId: packingTemplateIdFormat.schema.nullish(),
  stockout_date: z.instanceof(Temporal.PlainDate).nullish(),
  reorderThreshold: z.number().nullish(),
  isBundle: z.boolean(),
  trackLotFulfillment: z.boolean(),

  // Reorder parameters
  moq: z.number().nullish(),
  leadTime: z.number().nullish(),
  buffer: z.number().nullish(),
  coverageDays: z.number().nullish(),
  forecastingEnabled: z.boolean(),
});

export type ProductDetails = z.infer<typeof ProductDetailsSchema>;

export enum GeneralProductField {
  sku = "sku",
  weight = "weight",
  upc = "upc",
  hsCode = "hsCode",
  originCountry = "originCountry",
}
export const GeneralProductFieldSchema = createEnum(GeneralProductField);
export const GeneralProductAttributeType = {
  [GeneralProductField.sku]: AttributeType.ShortText,
  [GeneralProductField.weight]: AttributeType.Weight,
  [GeneralProductField.upc]: AttributeType.ShortText,
  [GeneralProductField.hsCode]: AttributeType.HsCode,
  [GeneralProductField.originCountry]: AttributeType.ShortText,
} satisfies Record<GeneralProductField, AttributeType>;

export const GeneralProductAttributeSchema = {
  [GeneralProductField.sku]:
    AttributeValueSchema[
      GeneralProductAttributeType[GeneralProductField.sku]
    ].nullable(),
  [GeneralProductField.weight]:
    AttributeValueSchema[
      GeneralProductAttributeType[GeneralProductField.weight]
    ].nullable(),
  [GeneralProductField.upc]:
    AttributeValueSchema[
      GeneralProductAttributeType[GeneralProductField.upc]
    ].nullable(),
  [GeneralProductField.hsCode]:
    AttributeValueSchema[
      GeneralProductAttributeType[GeneralProductField.hsCode]
    ].nullable(),
  [GeneralProductField.originCountry]:
    AttributeValueSchema[
      GeneralProductAttributeType[GeneralProductField.originCountry]
    ].nullable(),
};

export function getGeneralProductAttributeType<T extends GeneralProductField>(
  field: T,
): (typeof GeneralProductAttributeType)[T] {
  return GeneralProductAttributeType[field];
}

export type GeneralProductAttributeValueType = {
  [key in GeneralProductField]: (typeof GeneralProductAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getGeneralProductAttributeValue<T extends GeneralProductField>(
  field: T,
  value: unknown,
): GeneralProductAttributeValueType[T] | null {
  return GeneralProductAttributeSchema[field].parse(
    value,
  ) as GeneralProductAttributeValueType[T];
}

export function getProductAttributeValue<T extends GeneralProductField>({
  listing,
  field,
}: {
  listing: ProductDetails;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case GeneralProductField.sku:
      fieldValue = listing.sku;
      break;
    case GeneralProductField.weight:
      fieldValue = listing.weight;
      break;
    case GeneralProductField.upc:
      fieldValue = listing.upc;
      break;
    case GeneralProductField.hsCode:
      fieldValue = listing.hsCode;
      break;
    case GeneralProductField.originCountry:
      fieldValue = listing.originCountry;
      break;
    default:
      assertNever(field);
  }
  return GeneralProductAttributeSchema[field].parse(fieldValue);
}
